import { FadeIn, Tooltip } from "@xola/ui-kit";
import clsx from "clsx";
import { isEmpty, sumBy } from "lodash";
import React, { useCallback } from "react";
import { LineItem, LineItemObject, LineItemType, Purchase } from "types";
import shallow from "zustand/shallow";
import { getPartnerFeeLineItems } from "../../models/Purchase";
import { CreatePurchaseStep, useCreatePurchaseStore } from "../../stores/createPurchaseStore";
import { Breakdown } from "../Common/Breakdown";
import { BreakdownLineItem } from "../PurchaseModification/PaymentBreakdown";
import { CreatePurchaseApplyCode } from "./CreatePurchaseApplyCode";
import { CreatePurchaseBreakdown } from "./CreatePurchaseBreakdown";
import { CreatePurchaseSidebarSkeleton } from "./CreatePurchaseSidebarSkeleton";
import { getPurchaseItemsHavingId } from "./helpers/PurchaseCreateHelpers";
import { PurchaseItemSummary } from "./PurchaseItemSummary";
import { usePurchaseResponseStore } from "./store/usePurchaseResponseStore";
import { Currency } from "@/components/Common/Currency";
import { useSeller } from "@/hooks/useAccount";

export const CreateExperiencePurchaseSidebar = React.memo(() => {
    const { currency } = useSeller();
    const purchase = usePurchaseResponseStore((state) => state.purchase as Purchase);

    const [step, isOneStepCheckout] = useCreatePurchaseStore(
        (state) => [state.step, state.isOneStepCheckout()],
        shallow,
    );

    const activeItemIndex = useCreatePurchaseStore((state) => state.activeItemIndex);
    const showPayments = useCreatePurchaseStore((state) => !state.isWaitListPurchase);
    const showAddCode = useCreatePurchaseStore((state) => state.showAddCode(activeItemIndex));

    const handleEditClick = useCallback(
        (index: number) => () => {
            const { setActiveItemIndex, setPurchase } = useCreatePurchaseStore.getState().actions;
            setActiveItemIndex(index);
            setPurchase((draft) => getPurchaseItemsHavingId(draft));

            useCreatePurchaseStore.setState({ step: CreatePurchaseStep.SELECT_DATE });

            const createPurchaseScreenLayout = document.querySelector(`#columnLayoutFill`);
            if (createPurchaseScreenLayout) {
                (createPurchaseScreenLayout as HTMLElement).scroll(0, 0);
            }
        },
        [],
    );

    if (!purchase.id || isEmpty(purchase.items)) {
        return <CreatePurchaseSidebarSkeleton />;
    }

    const paymentLineItems = purchase.lineItems.filter((lineItem) => {
        return lineItem.type === LineItemType.PAYMENT || lineItem.type === LineItemType.AFFILIATE_DEPOSIT;
    });

    const hasPayments = paymentLineItems.length > 0;

    return (
        <div className="sticky top-0 space-y-6 px-10 py-7">
            <h3>Purchase Summary</h3>

            <div className="flex flex-col space-y-4">
                {purchase.items.map((item, index) => {
                    const isActive = index === activeItemIndex;
                    return (
                        <PurchaseItemSummary
                            key={item.id}
                            isActive={isActive}
                            itemIndex={index}
                            item={item}
                            canEdit={!isOneStepCheckout && step === CreatePurchaseStep.CHECKOUT}
                            onEditClick={handleEditClick(index)}
                        >
                            <CreatePurchaseBreakdown item={item} />
                        </PurchaseItemSummary>
                    );
                })}
            </div>

            <PartnerFee />
            <GrandTotal />

            {showPayments && hasPayments ? (
                <div
                    className="min-h-[150px] border-t border-black/10 px-0 py-6"
                    data-testid="create-purchase-sidebar-payment-summary"
                >
                    <h3 className="mb-4">Payment Summary</h3>

                    <FadeIn>
                        <Breakdown currency={currency}>
                            {paymentLineItems.map((lineItem) => (
                                <BreakdownLineItem key={lineItem.id} purchase={purchase} lineItem={lineItem} />
                            ))}

                            <Breakdown.Separator />
                            <Breakdown.SubtotalItem info="Total" value={Math.abs(sumBy(paymentLineItems, "amount"))} />
                        </Breakdown>
                    </FadeIn>
                </div>
            ) : null}

            {showAddCode && <CreatePurchaseApplyCode />}

            {showPayments && hasPayments && (
                <div className="border-t border-black/10 pt-6">
                    <Balance />
                </div>
            )}
        </div>
    );
});

CreateExperiencePurchaseSidebar.displayName = "CreateExperiencePurchaseSidebar";

export const PartnerFeeBreakdown = ({ lineItems }: { lineItems: LineItem[] }) => {
    const purchase = usePurchaseResponseStore((state) => state.purchase as Purchase);
    return (
        <div className="max-w-60 space-y-2 px-1 py-2">
            {lineItems.map((lineItem) => {
                const productName = purchase.items.find((item) => item.seller?.id === lineItem.seller?.id)?.name;
                const lineItemName = `${lineItem.name} - ${productName}`;
                return (
                    <div key={lineItem.id} className="flex justify-between space-x-2 text-sm">
                        <span>{lineItemName}</span>
                        <span>
                            <Currency shouldRemoveTrailingZeroes={false} currency={purchase.currency}>
                                {lineItem.amount}
                            </Currency>
                        </span>
                    </div>
                );
            })}
        </div>
    );
};

export const PartnerFee = React.memo(() => {
    const partnerFeeLineItems = usePurchaseResponseStore(
        (state) => getPartnerFeeLineItems(state.purchase as Purchase),
        shallow,
    );
    const showBreakdown = partnerFeeLineItems.length > 1;

    const partnerFee = partnerFeeLineItems.reduce((accumulator, lineItem) => accumulator + lineItem.amount, 0);
    const partnerFeeLineItem = partnerFeeLineItems[0];

    if (partnerFeeLineItems.length === 0) {
        return null;
    }

    return (
        <div className="border-b border-t border-gray-light py-4" data-testid="create-purchase-sidebar-partner-fee">
            <div className="flex justify-between font-bold">
                {showBreakdown ? <span>Service Fee</span> : <span>{partnerFeeLineItem.name}</span>}
                <Tooltip enabled={showBreakdown} content={<PartnerFeeBreakdown lineItems={partnerFeeLineItems} />}>
                    <span className={clsx(showBreakdown && "cursor-pointer border-b border-dashed")}>
                        <Currency shouldRemoveTrailingZeroes={false}>
                            {showBreakdown ? partnerFee : partnerFeeLineItem.amount}
                        </Currency>
                    </span>
                </Tooltip>
            </div>
        </div>
    );
});

PartnerFee.displayName = "PartnerFee";

export const GrandTotal = React.memo(() => {
    const amount = usePurchaseResponseStore((state) => state.purchase.amount);
    return (
        <h1 className="flex justify-between">
            <span>Grand Total</span>

            <span>
                <Currency shouldRemoveTrailingZeroes={false}>{amount}</Currency>
            </span>
        </h1>
    );
});

GrandTotal.displayName = "GrandTotal";

export const Balance = React.memo(() => {
    const dueNow = usePurchaseResponseStore((state) => state.purchase.dueNow);
    const hasPayment = usePurchaseResponseStore((state) =>
        state.purchase.lineItems?.some((lineItem) => lineItem.object === LineItemObject.PAYMENT),
    );

    return (
        <h1 className="flex justify-between text-danger" data-testid="create-purchase-sidebar-balance-due">
            <span>Balance Due {hasPayment && "Now"}</span>

            <span>
                <Currency shouldRemoveTrailingZeroes={false}>{dueNow}</Currency>
            </span>
        </h1>
    );
});

Balance.displayName = "Balance";
